import { Link } from 'react-router-dom'
import { Faq } from '../components/Faq'
import { ContactSalesPricing } from '../components/marketing/ContactSalesPricing'
import { useReveal } from '../components/marketing/motion'
import { SHOW_PUBLIC_PRICING } from '../config/pricing-visibility'
import faqs from '../data/faqs.json'
import { checkoutHref } from '../lib/checkout'
import { usePageMeta } from '../lib/usePageMeta'

interface Tier {
  name: string
  price: string
  period: string
  who: string
  features: string[]
  cta: string
  plan?: string
  highlight?: boolean
  /** Reassurance line rendered under the CTA button. */
  note?: string
}

const TIERS: Tier[] = [
  {
    name: 'Free',
    price: '€0',
    period: 'forever',
    who: 'Try it on a few lanes before you subscribe.',
    features: [
      'Compare top carriers on demo lanes',
      'MCP demo key (smcp_demo_public)',
      'On-time % + transit on sample routes',
      'Community support',
    ],
    cta: 'Search free, no card',
  },
  {
    name: 'Starter',
    price: '€49',
    period: 'per month',
    who: 'For shippers & small forwarders booking regularly.',
    features: [
      'Every lane, every carrier: full coverage',
      'Real on-time % + transit p50/p90 + sailing frequency',
      'Your own API key + MCP key (Claude, Cursor, …)',
      'Up to 1,000 queries / month',
      '90-day reliability history',
      'Email support · 1 seat',
    ],
    cta: 'Get your keys',
    plan: 'starter',
    highlight: true,
    note: 'Instant activation · Cancel anytime · VAT invoice',
  },
  {
    name: 'Business',
    price: '€199',
    period: 'per month',
    who: 'For teams routing serious volume across many lanes.',
    features: [
      'Everything in Starter',
      'Carrier league tables across all your lanes',
      'Reliability-drop alerts on lanes you watch',
      '12-month history · 10,000 queries / month',
      '5 seats · priority support',
    ],
    cta: 'Get your keys',
    plan: 'business',
  },
]

const VALUE = [
  {
    t: 'Reliability you can’t get from carriers',
    d: 'On-time % and transit p50/p90 computed from real actual-vs-scheduled arrivals across tracked port calls, not the sailing times carriers advertise.',
  },
  {
    t: 'Right inside your tools',
    d: 'Query it from Claude, Cursor, or Windsurf over MCP, or from any app via the REST API. Ask “who’s most reliable Shanghai→Rotterdam?” and get the ranking before you call the carrier.',
  },
  {
    t: 'A focused, standalone product',
    d: 'SchedulesMCP is its own subscription. Buy just what you need, monthly, cancel anytime. Already on TrackingMCP? The same login works, billed separately. No bundle required.',
  },
]

// FAQ text is the single-source-of-truth in src/data/faqs.json, shared with the prerender so the
// static HTML, the hydrated app, and the FAQPage JSON-LD stay identical.
const FAQ = faqs.pricing

export function PricingPage() {
  usePageMeta(
    'Pricing · SchedulesMCP',
    'SchedulesMCP pricing: ocean carrier schedule comparison, reliability scoring, and MCP access for cargo owners and forwarders.'
  )
  useReveal('main')
  return (
    <main className="max-w-6xl mx-auto px-6 pt-12 pb-10">
      <p
        className="m-0 font-mono text-xs uppercase text-accent text-center"
        style={{ letterSpacing: '0.14em' }}
      >
        Pricing
      </p>
      <h1
        className="font-display text-4xl mb-3 text-center"
        style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
      >
        Know which carrier to book
      </h1>
      <p className="text-[var(--text-secondary)] mb-2 text-center max-w-2xl mx-auto leading-relaxed">
        Pick a lane, see which carriers actually hit schedule, and know who to book before you call
        them, from the web, or query the full dataset from your own tools over the API and MCP.
        {SHOW_PUBLIC_PRICING ? ' Per month, cancel anytime.' : ' Priced to your volume.'}
      </p>
      <p className="text-sm text-[var(--text-secondary)] text-center mb-2 max-w-2xl mx-auto">
        The lane search on this site is the live engine. What you see is what the API returns.
      </p>
      <p className="text-[var(--text-primary)] font-medium text-center mb-2 max-w-2xl mx-auto leading-relaxed">
        One rollover, or one carrier that misses its window, costs days of demurrage and lost sales.
        Booking the line that actually hits schedule pays for the subscription many times over.
      </p>
      <p className="text-xs text-[var(--text-muted)] text-center mb-12">
        A standalone subscription, sister product to TrackingMCP, sold separately.
      </p>

      {!SHOW_PUBLIC_PRICING && <ContactSalesPricing />}

      {SHOW_PUBLIC_PRICING && (
        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {TIERS.map((t) => (
            <div
              key={t.name}
              className={`cp-card flex flex-col rounded-2xl p-6 bg-surface-0 border ${
                t.highlight ? 'border-signal shadow-[0_0_0_1px_var(--signal)]' : 'border-border'
              }`}
            >
              <div className="h-5 mb-1">
                {t.highlight && (
                  <span className="text-[10px] font-mono uppercase tracking-widest text-signal">
                    Most popular
                  </span>
                )}
              </div>
              <h2 className="font-display font-bold text-2xl">{t.name}</h2>
              <div className="mt-3 flex items-baseline gap-1.5">
                <span className="text-4xl font-mono font-semibold tabular-nums">{t.price}</span>
                <span className="text-sm text-[var(--text-muted)]">/ {t.period}</span>
              </div>
              <p className="text-sm text-[var(--text-secondary)] mt-3 mb-5 min-h-[40px]">{t.who}</p>
              <ul className="space-y-2.5 mb-8">
                {t.features.map((f) => (
                  <li key={f} className="text-sm text-[var(--text-secondary)] flex gap-2">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                      role="presentation"
                      className="text-signal shrink-0 mt-0.5"
                      style={{ width: '1em', height: '1em' }}
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {t.plan ? (
                <a
                  href={checkoutHref(t.plan)}
                  className={`mt-auto block text-center font-bold px-4 py-3 rounded-xl no-underline transition-opacity hover:opacity-90 ${
                    t.highlight
                      ? 'bg-signal text-on-accent'
                      : 'border border-border text-[var(--text-primary)] hover:border-signal/50'
                  }`}
                >
                  {t.cta}
                </a>
              ) : (
                <Link
                  to="/compare"
                  className="mt-auto block text-center font-bold px-4 py-3 rounded-xl no-underline border border-border text-[var(--text-primary)] hover:border-signal/50"
                >
                  {t.cta}
                </Link>
              )}
              {t.note && (
                <p className="mt-3 text-center text-xs text-[var(--text-muted)]">{t.note}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* What you're paying for */}
      <section className="mt-20">
        <h2 className="font-display font-light tracking-[-0.015em] text-2xl text-center mb-8">
          What you’re paying for
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {VALUE.map((v) => (
            <div key={v.t} className="cp-card bg-surface-0 border border-border rounded-2xl p-6">
              <h3 className="font-display font-bold text-base mb-2">{v.t}</h3>
              <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{v.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ — shared component emits matching FAQPage JSON-LD for answer engines. */}
      <Faq id="pricing" items={FAQ} heading="Pricing questions" />
    </main>
  )
}
